import { axiosInstance } from '../helper/axios.js';
import api from '../api/global';
import store from './index';

export default {
  namespaced: true,

  state: {
    userData: null,
    authToken: null,
  },

  getters: {
    userData: state => state.userData,
    isAuthenticated: state => !!state.authToken && !!state.userData,
  },

  mutations: {
    setUserData(state, user) {
      state.userData = user;
    },
    setAuthToken(state, token) {
      state.authToken = token;
    },
    clearAuthData(state) {
      state.userData = null;
      state.authToken = null;
    },
    updateUserLocale(state, { langId, lang }) {
      if (!state.userData || !state.userData.user) {
        return;
      }

      state.userData = {
        ...state.userData,
        user: {
          ...state.userData.user,
          lang_id: langId,
          lang,
        },
      };
    },
  },

  actions: {
    // Action to verify token on app load if it exists in state
    async validateToken({ commit, state }) {
      if (!state.authToken) {
        return; // No token to validate
      }
      try {
        const response = await axiosInstance.get(api.profile.base());
        const authData = response.data.data;
        commit('setUserData', authData);

        const selectedFirm = store.state.selectedFirm;
        if (selectedFirm) {
          const refreshedFirm = authData.firms.find(f => f.id === selectedFirm.id);
          store.commit('setSelectedFirm', refreshedFirm || null);
        }
      } catch (error) {
        console.error('Token validation failed:', error);
        // The axios interceptor will handle the 401 error, clear auth data, and redirect.
        // We just need to ensure the local state is also cleared.
        commit('clearAuthData');
        store.commit('setSelectedFirm', null); // Clear firm from root state
      }
    },

    // Login
    async sendLoginRequest({ commit }, data) {
      commit('setErrors', {}, { root: true });
      const response = await axiosInstance.post(api.auth.login(), data);

      if (!['login_validation_error', 'credentialing_error', 'unconfirmed_account'].includes(response.data.status)) {
        const authData = response.data.data;
        const authToken = response.data.token;

        commit('setUserData', authData);
        commit('setAuthToken', authToken);

        // After login, if a firm was previously selected and is still valid, keep it.
        // Otherwise, vuex-persistedstate will keep the old invalid one.
        const selectedFirm = store.state.selectedFirm;
        if (selectedFirm) {
          const refreshedFirm = authData.firms.find(f => f.id === selectedFirm.id);
          store.commit('setSelectedFirm', refreshedFirm || null);
        }
      }
      return response;
    },

    // HappyGast Login
    async sendHappyGastLoginRequest({ commit }, data) {
      commit('setErrors', {}, { root: true });
      const response = await axiosInstance.post(api.auth.happygastLogin(), data);

      if (response.status === 200) {
        const authData = response.data.data;
        const authToken = response.data.token;

        commit('setUserData', authData);
        commit('setAuthToken', authToken);

        if (authData.selected_firm) {
          store.commit('setSelectedFirm', authData.selected_firm);
        }
      }
      return response;
    },

    // Register
    async sendRegisterRequest({ commit }, data) {
      commit('setErrors', {}, { root: true });
      const response = await axiosInstance.post(api.auth.register(), data);

      if (!['error', 'Unconfirmed Account'].includes(response.data.status)) {
        const authData = response.data.data;
        const authToken = response.data.token;
        
        commit('setUserData', authData);
        commit('setAuthToken', authToken);
      }
      return response;
    },

    async updateLocale({ commit, state }, locale) {
      if (!state.authToken || !state.userData) {
        return null;
      }

      const response = await axiosInstance.put(api.profile.updateLocale(), { locale });
      const user = response.data.user;

      if (user) {
        commit('updateUserLocale', {
          langId: user.lang_id,
          lang: user.lang || { iso_code: response.data.locale },
        });
      }

      return response;
    },

    // Logout
    async sendLogoutRequest({ commit }) {
      try {
        // The axios interceptor will add the token to this request
        await axiosInstance.post(api.auth.logout());
      } catch (error) {
        console.error("Logout API call failed, proceeding with client-side cleanup.", error);
      } finally {
        // The response interceptor on 401 will also clear data, but we do it here
        // proactively to ensure the UI updates immediately.
        commit("clearAuthData");
        store.commit("setSelectedFirm", null);

        // Navigate to Login with full reload to avoid vue-router guard redirect warning
        const currentPath = window.location.pathname;
        const search = new URLSearchParams({ from: currentPath }).toString();
        window.location.replace(`/login?${search}`);
      }
    },
  },
};
